class DescriptionBlock extends HTMLElement {
  constructor() {
    super();    
    this.handleToggle = this.handleToggle.bind(this);
    this.isExpanded = false;
    this.originalContent = '';
    this.truncatedContent = '';
  }

  connectedCallback() {
    try {
      this.init();
    } catch (error) {
      console.error('DescriptionBlock initialization error:', error);
    }
  }

  init() {
    this.contentElement = this.querySelector('.description-block__content');
    this.toggleButton = this.querySelector('.description-block__toggle-button');
    
    if (!this.contentElement) {
      return;
    }

    if (!this.toggleButton) {
      return;
    }

    this.dataset.loaded = 'true';
    this.displayMode = this.dataset.displayMode === 'true';
    this.wordsLimit = parseInt(this.dataset.wordsLimit, 10) || 50;

    this.originalContent = this.contentElement.innerHTML.trim();

    if (!this.displayMode) {
      this.hideToggleButton();
      return;
    }

    const wordCount = this.getWordCount(this.contentElement.textContent);
    
    if (wordCount <= this.wordsLimit) {
      this.hideToggleButton();
      return;
    }

    this.truncatedContent = this.truncateContent(this.originalContent, this.wordsLimit);
    this.contentElement.innerHTML = this.truncatedContent;
    
    this.showToggleButton();
    this.toggleButton.addEventListener('click', this.handleToggle);
  }

  /**
   * Get word count from text
   * @param {string} text 
   * @returns {number}
   */
  getWordCount(text) {
    if (!text || typeof text !== 'string') return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  }

  /**
   * Truncate HTML content to specified word limit
   * @param {string} html 
   * @param {number} limit 
   * @returns {string}
   */
  truncateContent(html, limit) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    const textContent = tempDiv.textContent || tempDiv.innerText;
    const words = textContent.trim().split(/\s+/);
    
    if (words.length <= limit) {
      return html;
    }

    const truncatedWords = words.slice(0, limit);
    const truncatedText = truncatedWords.join(' ') + '...';
    
    return this.preserveHTMLStructure(tempDiv, truncatedText);
  }

  /**
   * Attempt to preserve HTML structure when truncating
   * @param {HTMLElement} element 
   * @param {string} targetText 
   * @returns {string}
   */
  preserveHTMLStructure(element, targetText) {
    const clone = element.cloneNode(true);
    const walker = document.createTreeWalker(
      clone,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    let currentLength = 0;
    const words = targetText.split(/\s+/);
    const targetLength = words.length;
    let lastTextNode = null;

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const nodeWords = node.textContent.trim().split(/\s+/).filter(w => w);
      
      if (currentLength + nodeWords.length <= targetLength) {
        currentLength += nodeWords.length;
        lastTextNode = node;
      } else {
        const remainingWords = targetLength - currentLength;
        if (remainingWords > 0) {
          node.textContent = nodeWords.slice(0, remainingWords).join(' ') + '...';
          lastTextNode = node;
        } else if (lastTextNode) {
          lastTextNode.textContent += '...';
        }
        
        // Remove subsequent nodes
        let nextNode = walker.nextNode();
        while (nextNode) {
          const toRemove = nextNode;
          nextNode = walker.nextNode();
          if (toRemove.parentNode) {
            toRemove.parentNode.removeChild(toRemove);
          }
        }
        break;
      }
    }

    return clone.innerHTML;
  }

  /**
   * Handle toggle button click
   */
  handleToggle() {
    this.isExpanded = !this.isExpanded;
    
    if (this.isExpanded) {
      this.contentElement.innerHTML = this.originalContent;
      this.toggleButton.setAttribute('aria-expanded', 'true');
    } else {
      this.contentElement.innerHTML = this.truncatedContent;
      this.toggleButton.setAttribute('aria-expanded', 'false');
    }

    // Update button visibility states
    this.updateToggleButtonState();
  }

  updateToggleButtonState() {
    const moreText = this.toggleButton.querySelector('.description-block__toggle-text--more');
    const lessText = this.toggleButton.querySelector('.description-block__toggle-text--less');

    if (moreText && lessText) {
      if (this.isExpanded) {
        moreText.style.display = 'none';
        lessText.style.display = 'inline';
      } else {
        moreText.style.display = 'inline';
        lessText.style.display = 'none';
      }
    }
  }

  hideToggleButton() {
    if (this.toggleButton) {
      this.toggleButton.style.display = 'none';
      this.toggleButton.setAttribute('aria-hidden', 'true');
    }
  }

  showToggleButton() {
    if (this.toggleButton) {
      this.toggleButton.style.display = '';
      this.toggleButton.setAttribute('aria-hidden', 'false');
      this.updateToggleButtonState();
    }
  }

  disconnectedCallback() {
    if (this.toggleButton) {
      this.toggleButton.removeEventListener('click', this.handleToggle);
    }
  }
}

if ('customElements' in window) {
  customElements.define('description-block', DescriptionBlock);
} else {
  console.warn('Custom Elements not supported in this browser');
}